/**
 * Lightweight alternative to SlicePanel's AI/manual slicing: a batch of title-only rows,
 * each with its own node type, created as children of a Solution node. Unlike SlicePanel
 * (one shared type per batch), every row here resolves to its own NodeType.
 */
import { useEffect, useRef, useState } from 'react';
import { COLORS } from 'utils/canvasUtils';
import { OstNode } from 'types';
import { Modal, Button, IconButton } from '../ui';

type ManualSliceRowType = 'enablerNode' | 'story' | 'experiment';

type ManualSliceRow = {
    title: string;
    type: ManualSliceRowType;
};

type ManualSliceModalProps = {
    parentNode: OstNode;
    onClose: () => void;
    onCreate: (rows: ManualSliceRow[]) => void;
};

const TYPE_OPTS: { value: ManualSliceRowType; label: string }[] = [
    { value: 'story', label: 'Story' },
    { value: 'experiment', label: 'Experiment' },
    { value: 'enablerNode', label: 'Enabler' },
];

export default function ManualSliceModal({ onClose, onCreate }: ManualSliceModalProps) {
    const defaultRow: ManualSliceRow = { title: '', type: 'story' };
    const [rows, setRows] = useState<ManualSliceRow[]>([{ ...defaultRow }]);
    // Index of a just-added row whose title input should receive focus once it mounts —
    // set by the Tab-to-add-row handler below, consumed by the effect right after it.
    const [focusRowIndex, setFocusRowIndex] = useState<number | null>(null);
    const titleRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (focusRowIndex === null) return;
        titleRefs.current[focusRowIndex]?.focus();
        setFocusRowIndex(null);
    }, [rows, focusRowIndex]);

    const setRowTitle = (idx: number, value: string) => {
        setRows((old) => old.map((r, i) => (i === idx ? { ...r, title: value } : r)));
    };

    const setRowType = (idx: number, value: ManualSliceRowType) => {
        setRows((old) => old.map((r, i) => (i === idx ? { ...r, type: value } : r)));
    };

    const addRow = () => setRows((old) => [...old, { ...defaultRow }]);
    const removeRow = (idx: number) => setRows((old) => old.filter((_, i) => i !== idx));

    /** Tab out of the last row's type dropdown adds a new row and focuses its title,
     * instead of tabbing on to the + Add item / Cancel / OK controls. */
    const handleTypeKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>, idx: number) => {
        if (e.key !== 'Tab' || e.shiftKey || idx !== rows.length - 1) return;
        e.preventDefault();
        setFocusRowIndex(rows.length);
        addRow();
    };

    const create = () => {
        const valid = rows
            .map((r) => ({ ...r, title: r.title.trim() }))
            .filter((r) => r.title);
        if (valid.length === 0) {
            alert('Please add at least one item with a title');
            return;
        }
        onCreate(valid);
        onClose();
    };

    return (
        <Modal
            title="Add action items"
            onClose={onClose}
            headerVariant='solution'
            closeOnOverlayClick
        >
            <div className='manual-slice-list'>
                {rows.map((row, idx) => (
                    <div key={idx} className='manual-slice-row'>
                        <span
                            className='manual-slice-row__swatch'
                            style={{ '--priority-color': COLORS[row.type].border } as React.CSSProperties}
                        />
                        <input
                            ref={(el) => { titleRefs.current[idx] = el; }}
                            className='form-input manual-slice-row__title'
                            value={row.title}
                            onChange={(e) => setRowTitle(idx, e.target.value)}
                            placeholder='Item title'
                            autoFocus={idx === 0}
                        />
                        <select
                            className='form-select manual-slice-row__type'
                            value={row.type}
                            onChange={(e) => setRowType(idx, e.target.value as ManualSliceRowType)}
                            onKeyDown={(e) => handleTypeKeyDown(e, idx)}
                        >
                            {TYPE_OPTS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <span className='manual-slice-row__remove'>
                            <IconButton tabIndex={-1} onClick={() => removeRow(idx)}>✕</IconButton>
                        </span>
                    </div>
                ))}
            </div>

            <div className='manual-slice-add-row'>
                <span className='manual-slice-row__swatch manual-slice-row__swatch--spacer' />
                <button className='manual-slice-add-btn' onClick={addRow} type='button'>
                    + Add item
                </button>
                <span className='manual-slice-row__remove' />
            </div>

            <div className='modal-actions'>
                <Button variant='secondary' onClick={onClose}>
                    Cancel
                </Button>
                <Button variant='primary' onClick={create}>
                    OK
                </Button>
            </div>
        </Modal>
    );
}
